import React, { useState } from 'react';
import {CreateConsentResponse} from '../blinkpay';
import {createConsent} from '../demoBffClientInstance';

interface GatewayFlowProps {
    onSubmit?: (payload: any) => void;
    onClose?: () => void;
}

const GatewayFlow: React.FC<GatewayFlowProps> = ({ onSubmit, onClose }) => {
    const [redirectUri, setRedirectUri] = useState<string>(process.env.REACT_APP_BLINKPAY_REDIRECT_URI || 'http://localhost:3000/redirect');
    const [permissions, setPermissions] = useState({
        ReadAccounts: true,
        ReadBalances: true,
        ReadTransactions: true,
        ReadStatements: false,
        ReadParty: true
    });
    const [expirationDateTime, setExpirationDateTime] = useState<string>('');
    const [startDateTime, setStartDateTime] = useState<string>('');
    const [endDateTime, setEndDateTime] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    const handlePermissionChange = (permission: string) => {
        setPermissions(prev => ({
            ...prev,
            [permission]: !prev[permission as keyof typeof prev]
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const selectedPermissions = Object.entries(permissions)
            .filter(([_, selected]) => selected)
            .map(([permission]) => permission);

        const payload: any = {
            flow: {
                detail: {
                    type: 'gateway',
                    redirect_uri: redirectUri
                }
            },
            permissions: selectedPermissions
        };

        if (expirationDateTime) {
            payload.expiration_date_time = new Date(expirationDateTime).toISOString();
        }
        if (startDateTime) {
            payload.start_date_time = new Date(startDateTime).toISOString();
        }
        if (endDateTime) {
            payload.end_date_time = new Date(endDateTime).toISOString();
        }

        console.log('Gateway Flow Payload:', payload);

        try {
            // Call the Blink API to create consent
            console.log('Creating consent...');

            const response: CreateConsentResponse = await createConsent(payload);
            console.log('Consent created successfully:', response);

            if (response?.consentId) {
                console.log('Consent ID:', response.consentId);

                // Store consent ID in localStorage for demo purposes
                localStorage.setItem('consentId', response.consentId);

                // If there's a redirect URL in the response, redirect to it
                if (response.redirectUri) {
                    console.log('Redirecting to:', response.redirectUri);
                    window.location.href = response.redirectUri;
                } else {
                    // If no redirect URI, navigate to redirect page with consent ID
                    window.location.href = `/redirect?cid=${response.consentId}`;
                }
            } else {
                setError('No consent ID received from server');
            }

            if (onSubmit) {
                onSubmit(response);
            }
        } catch (error: any) {
            console.error('Error creating consent:', error);
            console.error('Error details:', {
                message: error?.message,
                response: error?.response,
                stack: error?.stack
            });

            // Check various error types
            let errorMessage = 'Failed to create consent';
            if (error?.message?.includes('Failed to refresh access token')) {
                errorMessage = 'Failed to authenticate with Blink API. Please check your client credentials.';
            } else if (error?.message?.includes('Network Error') || error?.code === 'ECONNREFUSED') {
                errorMessage = 'Cannot connect to Blink API. Please check your REACT_APP_BLINKPAY_API_GATEWAY_URL configuration.';
            } else if (error?.response?.status === 401) {
                errorMessage = 'Authentication failed. Please check your API credentials.';
            } else if (error?.response?.status === 400) {
                errorMessage = 'Invalid request: ' + (error?.response?.data?.message || 'Please check the request payload.');
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="gateway-flow-modal">
            <div className="gateway-flow-container">
                <div className="gateway-flow-header">
                    <h2>Gateway Flow Configuration</h2>
                    {onClose && (
                        <button className="close-btn" onClick={onClose}>×</button>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="gateway-flow-form">
                    <div className="form-section">
                        <h3>Redirect Configuration</h3>
                        <div className="form-group">
                            <label htmlFor="redirect-uri">Redirect URI:</label>
                            <input
                                id="redirect-uri"
                                type="url"
                                value={redirectUri}
                                onChange={(e) => setRedirectUri(e.target.value)}
                                required
                                className="form-input"
                            />
                            <span className="help-text">
                                The URI to redirect the user to after gateway authentication
                            </span>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Permissions</h3>
                        <div className="permissions-grid">
                            {Object.entries(permissions).map(([permission, checked]) => (
                                <label key={permission} className="permission-option">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => handlePermissionChange(permission)}
                                    />
                                    <span>{permission.replace(/([A-Z])/g, ' $1').trim()}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="form-section">
                        <h3>Date/Time Configuration (Optional)</h3>
                        <div className="datetime-grid">
                            <div className="form-group">
                                <label htmlFor="start-datetime">Start Date/Time:</label>
                                <input
                                    id="start-datetime"
                                    type="datetime-local"
                                    value={startDateTime}
                                    onChange={(e) => setStartDateTime(e.target.value)}
                                    className="form-input"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="end-datetime">End Date/Time:</label>
                                <input
                                    id="end-datetime"
                                    type="datetime-local"
                                    value={endDateTime}
                                    onChange={(e) => setEndDateTime(e.target.value)}
                                    className="form-input"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="expiration-datetime">Expiration Date/Time (Null means indefinite):</label>
                                <input
                                        id="expiration-datetime"
                                        type="datetime-local"
                                        value={expirationDateTime}
                                        onChange={(e) => setExpirationDateTime(e.target.value)}
                                        className="form-input"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="submit-btn" disabled={loading}>
                            {loading ? 'Creating Consent...' : 'Initiate Gateway Flow'}
                        </button>
                        {onClose && (
                            <button type="button" onClick={onClose} className="cancel-btn" disabled={loading}>
                                Cancel
                            </button>
                        )}
                    </div>

                    {error && (
                            <div className="error-message">
                                {error}
                            </div>
                    )}
                </form>

                {/* Gateway Info */}
                <div className="gateway-info">
                    <h4>About Gateway Flow</h4>
                    <p>
                        The Gateway flow provides a bank-agnostic authentication method. Users will be presented
                        with a unified interface to select their bank and complete the authentication process.
                    </p>
                    <ul>
                        <li>No need to specify a bank upfront</li>
                        <li>Consistent user experience across all banks</li>
                        <li>Simplified integration with single redirect URI</li>
                    </ul>
                </div>
            </div>

            <style>{`
                .gateway-flow-modal {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 20px;
                    overflow-y: auto;
                }

                .gateway-flow-container {
                    background: white;
                    border-radius: 20px;
                    max-width: 800px;
                    width: 100%;
                    max-height: 90vh;
                    overflow-y: auto;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                }

                .gateway-flow-header {
                    padding: 30px;
                    border-bottom: 2px solid #f0f0f0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .gateway-flow-header h2 {
                    margin: 0;
                    color: #333;
                    font-size: 24px;
                }

                .close-btn {
                    background: none;
                    border: none;
                    font-size: 30px;
                    color: #999;
                    cursor: pointer;
                    width: 40px;
                    height: 40px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    transition: all 0.3s ease;
                }

                .close-btn:hover {
                    background: #f0f0f0;
                    color: #333;
                }

                .gateway-flow-form {
                    padding: 30px;
                }

                .form-section {
                    margin-bottom: 30px;
                }

                .form-section h3 {
                    color: #555;
                    font-size: 18px;
                    margin-bottom: 15px;
                    padding-bottom: 10px;
                    border-bottom: 1px solid #e0e0e0;
                }

                .form-group {
                    margin-bottom: 20px;
                }

                .form-group label {
                    display: block;
                    color: #555;
                    font-weight: 500;
                    margin-bottom: 8px;
                }

                .form-input {
                    width: 100%;
                    padding: 12px;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    font-size: 16px;
                    background: #f8f9fa;
                    transition: all 0.3s ease;
                    box-sizing: border-box;
                }

                .form-input:focus {
                    outline: none;
                    border-color: #4facfe;
                    background: white;
                }

                .help-text {
                    display: block;
                    margin-top: 8px;
                    font-size: 14px;
                    color: #777;
                    font-style: italic;
                }

                .permissions-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 15px;
                }

                .permission-option {
                    display: flex;
                    align-items: center;
                    padding: 10px;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .permission-option:hover {
                    background: #f0f9ff;
                    border-color: #4facfe;
                }

                .permission-option input[type="checkbox"] {
                    margin-right: 10px;
                    accent-color: #4facfe;
                    width: 18px;
                    height: 18px;
                }

                .datetime-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 20px;
                }

                .form-actions {
                    display: flex;
                    gap: 15px;
                    justify-content: flex-end;
                    padding-top: 20px;
                    border-top: 1px solid #e0e0e0;
                }

                .submit-btn {
                    padding: 15px 30px;
                    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
                    color: white;
                    border: none;
                    border-radius: 10px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .submit-btn:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 5px 20px rgba(79, 172, 254, 0.4);
                }

                .cancel-btn {
                    padding: 15px 30px;
                    background: #f0f0f0;
                    color: #666;
                    border: none;
                    border-radius: 10px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .submit-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .error-message {
                    background: #f8d7da;
                    border: 1px solid #f5c6cb;
                    color: #721c24;
                    padding: 12px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                }

                .cancel-btn:hover {
                    background: #e0e0e0;
                    color: #333;
                }

                .gateway-info {
                    padding: 20px 30px 30px;
                    background: linear-gradient(135deg, rgba(79, 172, 254, 0.1) 0%, rgba(0, 242, 254, 0.1) 100%);
                    border-radius: 0 0 20px 20px;
                }

                .gateway-info h4 {
                    color: #333;
                    margin-bottom: 15px;
                    font-size: 16px;
                }

                .gateway-info p {
                    color: #555;
                    margin-bottom: 15px;
                    line-height: 1.6;
                }

                .gateway-info ul {
                    margin: 0;
                    padding-left: 20px;
                    color: #555;
                }

                .gateway-info li {
                    margin-bottom: 8px;
                }

                @media (max-width: 600px) {
                    .gateway-flow-modal {
                        padding: 0;
                    }

                    .gateway-flow-container {
                        border-radius: 0;
                        max-height: 100vh;
                    }

                    .permissions-grid {
                        grid-template-columns: 1fr;
                    }

                    .datetime-grid {
                        grid-template-columns: 1fr;
                    }

                    .form-actions {
                        flex-direction: column;
                    }

                    .submit-btn,
                    .cancel-btn {
                        width: 100%;
                    }
                }
            `}</style>
        </div>
    );
};

export default GatewayFlow;